/**
 * API Client Service
 * ==================
 * Axios-based HTTP client for Exodus API.
 */

import axios, { AxiosError, AxiosInstance } from 'axios'
import type {
  AddCredentialRequest,
  ApiResponse,
  Asset,
  AssetCreate,
  AssetLocation,
  AssetLocationCreate,
  AssetLocationUpdate,
  AssetLocationWithCount,
  AssetManufacturer,
  AssetManufacturerCreate,
  AssetManufacturerUpdate,
  AssetManufacturerWithCount,
  AssetSearchParams,
  AssetStats,
  AssetStatusUpdate,
  AssetUpdate,
  DiskHealthDrive,
  DiskHealthSearchParams,
  DiskHealthStats,
  QueryGatewayResponse,
  AuthStatus,
  BarcodeLookupResponse,
  Category,
  CategoryCreate,
  CategoryUpdate,
  CompatibilitySearchRequest,
  CompatibilitySearchResponse,
  CredentialResponse,
  HistoryEntry,
  HistoryEntryCreate,
  HistoryEntryUpdate,
  ImageGalleryItem,
  ImageGalleryParams,
  ImageSearchResponse,
  InfrastructureSource,
  IPLookupResponse,
  Location,
  LocationCreate,
  LocationUpdate,
  Manufacturer,
  ManufacturerCreate,
  ManufacturerUpdate,
  Motorcycle,
  MotorcycleCreate,
  MotorcycleImage,
  MotorcycleSummary,
  MotorcycleUpdate,
  MotoLocation,
  MotoLocationCreate,
  MotoLocationUpdate,
  MotoManufacturer,
  MotoManufacturerCreate,
  MotoManufacturerUpdate,
  PaginatedResponse,
  Product,
  ProductCreate,
  ProductImage,
  ProductImageCreate,
  ProductImageUpdate,
  ProductSearchParams,
  ProductUpdate,
  ServiceRecord,
  ServiceRecordCreate,
  ServiceRecordUpdate,
  SKULookupResponse,
  User,
  Ticket,
  TicketAttachment,
  TicketComment,
  TicketCommentCreate,
  TicketCreate,
  TicketSearchParams,
  TicketStats,
  TicketSummary,
  TicketUpdate,
  TimelogCustomer,
  TimelogCustomerCreate,
  TimelogCustomerUpdate,
  TimelogEntry,
  TimelogEntryCreate,
  TimelogEntryUpdate,
  TimelogSearchParams,
  Vendor,
  VendorCreate,
  VendorUpdate,
} from '@/types'

// =============================================================================
// API Client Setup
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests for auth
})

// Request interceptor - cookies handle auth, prevent browser caching
apiClient.interceptors.request.use(
  (config) => {
    // Auth is handled via httpOnly cookies set by the backend
    // Prevent browser from caching API responses (ensures fresh data on refetch)
    config.headers['Cache-Control'] = 'no-cache'
    config.headers['Pragma'] = 'no-cache'
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle 401 errors
    if (error.response?.status === 401) {
      // Don't redirect for auth endpoints (they're expected to fail when not logged in)
      const url = error.config?.url || ''
      if (!url.includes('/auth/')) {
        // Redirect to login for protected endpoints
        window.location.href = `${API_BASE_URL}/auth/login?redirect_url=${encodeURIComponent(window.location.pathname)}`
      }
    }
    return Promise.reject(error)
  }
)

// =============================================================================
// Product API
// =============================================================================

export const productApi = {
  list: async (params?: ProductSearchParams): Promise<PaginatedResponse<Product>> => {
    const response = await apiClient.get<PaginatedResponse<Product>>('/products', { params })
    return response.data
  },

  get: async (id: string | number): Promise<ApiResponse<Product>> => {
    const response = await apiClient.get<ApiResponse<Product>>(`/products/${id}`)
    return response.data
  },

  create: async (data: ProductCreate): Promise<ApiResponse<Product>> => {
    const response = await apiClient.post<ApiResponse<Product>>('/products', data)
    return response.data
  },

  update: async (id: string | number, data: ProductUpdate): Promise<ApiResponse<Product>> => {
    const response = await apiClient.put<ApiResponse<Product>>(`/products/${id}`, data)
    return response.data
  },

  delete: async (id: string | number): Promise<void> => {
    await apiClient.delete(`/products/${id}`)
  },
}

// =============================================================================
// Product Image API
// =============================================================================

export const productImageApi = {
  /**
   * Add an image by URL
   */
  addByUrl: async (
    productId: string | number,
    data: ProductImageCreate
  ): Promise<ApiResponse<ProductImage>> => {
    const response = await apiClient.post<ApiResponse<ProductImage>>(
      `/products/${productId}/images`,
      data
    )
    return response.data
  },

  /**
   * Upload an image file
   */
  upload: async (
    productId: string | number,
    file: File,
    isPrimary = false
  ): Promise<ApiResponse<ProductImage>> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('is_primary', String(isPrimary))

    const response = await apiClient.post<ApiResponse<ProductImage>>(
      `/products/${productId}/images/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  /**
   * Update image metadata
   */
  update: async (
    productId: string | number,
    imageId: number,
    data: ProductImageUpdate
  ): Promise<ApiResponse<ProductImage>> => {
    const response = await apiClient.patch<ApiResponse<ProductImage>>(
      `/products/${productId}/images/${imageId}`,
      data
    )
    return response.data
  },

  /**
   * Delete an image
   */
  delete: async (productId: string | number, imageId: number): Promise<void> => {
    await apiClient.delete(`/products/${productId}/images/${imageId}`)
  },

  /**
   * Set an image as primary
   */
  setPrimary: async (
    productId: string | number,
    imageId: number
  ): Promise<ApiResponse<ProductImage>> => {
    return productImageApi.update(productId, imageId, { is_primary: true })
  },
}

// =============================================================================
// Category API
// =============================================================================

export const categoryApi = {
  list: async (includeChildren = false): Promise<ApiResponse<Category[]>> => {
    const response = await apiClient.get<ApiResponse<Category[]>>('/categories', {
      params: { include_children: includeChildren },
    })
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<Category>> => {
    const response = await apiClient.get<ApiResponse<Category>>(`/categories/${id}`)
    return response.data
  },

  create: async (data: CategoryCreate): Promise<ApiResponse<Category>> => {
    const response = await apiClient.post<ApiResponse<Category>>('/categories', data)
    return response.data
  },

  update: async (id: number, data: CategoryUpdate): Promise<ApiResponse<Category>> => {
    const response = await apiClient.put<ApiResponse<Category>>(`/categories/${id}`, data)
    return response.data
  },

  delete: async (id: number, reassignTo?: number): Promise<void> => {
    await apiClient.delete(`/categories/${id}`, {
      params: reassignTo ? { reassign_to: reassignTo } : undefined,
    })
  },
}

// =============================================================================
// Location API
// =============================================================================

export const locationApi = {
  list: async (includeCounts = false): Promise<ApiResponse<Location[]>> => {
    const response = await apiClient.get<ApiResponse<Location[]>>('/locations', {
      params: { include_counts: includeCounts },
    })
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<Location>> => {
    const response = await apiClient.get<ApiResponse<Location>>(`/locations/${id}`)
    return response.data
  },

  create: async (data: LocationCreate): Promise<ApiResponse<Location>> => {
    const response = await apiClient.post<ApiResponse<Location>>('/locations', data)
    return response.data
  },

  update: async (id: number, data: LocationUpdate): Promise<ApiResponse<Location>> => {
    const response = await apiClient.put<ApiResponse<Location>>(`/locations/${id}`, data)
    return response.data
  },

  delete: async (id: number, reassignTo?: number): Promise<void> => {
    await apiClient.delete(`/locations/${id}`, {
      params: reassignTo ? { reassign_to: reassignTo } : undefined,
    })
  },
}

// =============================================================================
// Manufacturer API
// =============================================================================

export const manufacturerApi = {
  list: async (includeCounts = false): Promise<ApiResponse<Manufacturer[]>> => {
    const response = await apiClient.get<ApiResponse<Manufacturer[]>>('/manufacturers', {
      params: { include_counts: includeCounts },
    })
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<Manufacturer>> => {
    const response = await apiClient.get<ApiResponse<Manufacturer>>(`/manufacturers/${id}`)
    return response.data
  },

  create: async (data: ManufacturerCreate): Promise<ApiResponse<Manufacturer>> => {
    const response = await apiClient.post<ApiResponse<Manufacturer>>('/manufacturers', data)
    return response.data
  },

  update: async (id: number, data: ManufacturerUpdate): Promise<ApiResponse<Manufacturer>> => {
    const response = await apiClient.put<ApiResponse<Manufacturer>>(`/manufacturers/${id}`, data)
    return response.data
  },

  delete: async (id: number, reassignTo?: number): Promise<void> => {
    await apiClient.delete(`/manufacturers/${id}`, {
      params: reassignTo ? { reassign_to: reassignTo } : undefined,
    })
  },
}

// =============================================================================
// Health API
// =============================================================================

export const healthApi = {
  check: async (): Promise<{ status: string; version: string }> => {
    const response = await apiClient.get('/health')
    return response.data
  },

  checkDb: async (): Promise<{ status: string; database: string }> => {
    const response = await apiClient.get('/health/db')
    return response.data
  },
}

// =============================================================================
// Auth API
// =============================================================================

// =============================================================================
// Barcode Lookup API
// =============================================================================

export interface BarcodeAnalysisResponse {
  barcode: string | null
  confidence: 'low' | 'medium' | 'high'
  error: string | null
}

export const barcodeApi = {
  /**
   * Look up product information by barcode (EAN/UPC/GTIN)
   */
  lookup: async (barcode: string): Promise<BarcodeLookupResponse> => {
    const response = await apiClient.post<BarcodeLookupResponse>('/barcode/lookup', { barcode })
    return response.data
  },

  /**
   * Analyze an image to extract barcode numbers using AI vision
   */
  analyzeImage: async (imageBase64: string): Promise<BarcodeAnalysisResponse> => {
    const response = await apiClient.post<BarcodeAnalysisResponse>('/barcode/analyze-image', { image: imageBase64 })
    return response.data
  },
}

// =============================================================================
// Image Search API
// =============================================================================

export const imageSearchApi = {
  /**
   * Search for product images using Google Custom Search
   */
  search: async (query: string, numResults: number = 20): Promise<ImageSearchResponse> => {
    const response = await apiClient.post<ImageSearchResponse>('/image-search/search', {
      query,
      num_results: numResults,
    })
    return response.data
  },
}

// =============================================================================
// SKU Lookup API
// =============================================================================

export const skuLookupApi = {
  /**
   * Look up product information by SKU/part number using AI
   */
  lookup: async (sku: string): Promise<SKULookupResponse> => {
    const response = await apiClient.post<SKULookupResponse>('/products/lookup-sku', { sku })
    return response.data
  },
}

// =============================================================================
// Compatibility Search API
// =============================================================================

export const compatibilitySearchApi = {
  /**
   * Search for compatible products/systems using AI
   * Searches manufacturer websites and compatibility databases
   */
  search: async (request: CompatibilitySearchRequest): Promise<CompatibilitySearchResponse> => {
    const response = await apiClient.post<CompatibilitySearchResponse>('/products/lookup-compatibility', request)
    return response.data
  },
}

// =============================================================================
// Service Tag Lookup API
// =============================================================================

export interface ServiceTagLookupRequest {
  service_tag: string
}

export interface ServiceTagLookupResponse {
  service_tag: string
  manufacturer: string | null
  model: string | null
  description: string | null
  ship_date: string | null
  warranty_end: string | null
  warranty_status: string | null
  specs: string | null
  confidence: 'low' | 'medium' | 'high'
  source_url: string | null
  source: 'dell_api' | 'ai_lookup' | null  // Data source indicator
  error: string | null
}

export const serviceTagApi = {
  /**
   * Look up device information by service tag using AI
   * Searches Dell/HP/Lenovo support pages and extracts device info
   * @param serviceTag - The service tag or serial number to look up
   * @param manufacturer - Optional manufacturer hint: 'dell', 'hp', 'lenovo', or 'auto'
   */
  lookup: async (serviceTag: string, manufacturer?: string): Promise<ServiceTagLookupResponse> => {
    const response = await apiClient.post<{ data: ServiceTagLookupResponse }>('/products/lookup-service-tag', {
      service_tag: serviceTag,
      manufacturer: manufacturer || null,
    })
    return response.data.data
  },
}

// =============================================================================
// Product Identification API
// =============================================================================

export interface ProductIdentificationImage {
  url: string
  thumbnail: string
  title: string
  width: number | null
  height: number | null
}

export interface ProductIdentificationProduct {
  name: string | null
  description: string | null
  manufacturer: string | null
  category: string | null
  compatible_products: string | null
  confidence: 'low' | 'medium' | 'high'
  search_query: string | null
}

export interface ProductIdentificationResponse {
  product: ProductIdentificationProduct | null
  images: ProductIdentificationImage[]
  error: string | null
}

export const productIdentifyApi = {
  /**
   * Identify a product from an image using AI vision
   */
  identify: async (imageBase64: string): Promise<ProductIdentificationResponse> => {
    const response = await apiClient.post<ProductIdentificationResponse>('/identify/product', { image: imageBase64 })
    return response.data
  },
}

// =============================================================================
// Image Gallery API
// =============================================================================

export const imageGalleryApi = {
  /**
   * List all product images with associated product info
   */
  list: async (params?: ImageGalleryParams): Promise<PaginatedResponse<ImageGalleryItem>> => {
    const response = await apiClient.get<PaginatedResponse<ImageGalleryItem>>('/images', { params })
    return response.data
  },
}

// =============================================================================
// Auth API
// =============================================================================

export const authApi = {
  /**
   * Check authentication status
   */
  status: async (): Promise<AuthStatus> => {
    const response = await apiClient.get<AuthStatus>('/auth/status')
    return response.data
  },

  /**
   * Get current user info (requires auth)
   */
  me: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me')
    return response.data
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout')
  },

  /**
   * Get login URL (redirects to Microsoft)
   */
  getLoginUrl: (redirectUrl?: string): string => {
    const base = `${API_BASE_URL}/auth/login`
    if (redirectUrl) {
      return `${base}?redirect_url=${encodeURIComponent(redirectUrl)}`
    }
    return base
  },
}

// =============================================================================
// Moto Location API
// =============================================================================

export const motoLocationApi = {
  list: async (): Promise<ApiResponse<MotoLocation[]>> => {
    const response = await apiClient.get<ApiResponse<MotoLocation[]>>('/moto/locations')
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<MotoLocation>> => {
    const response = await apiClient.get<ApiResponse<MotoLocation>>(`/moto/locations/${id}`)
    return response.data
  },

  create: async (data: MotoLocationCreate): Promise<ApiResponse<MotoLocation>> => {
    const response = await apiClient.post<ApiResponse<MotoLocation>>('/moto/locations', data)
    return response.data
  },

  update: async (id: number, data: MotoLocationUpdate): Promise<ApiResponse<MotoLocation>> => {
    const response = await apiClient.put<ApiResponse<MotoLocation>>(`/moto/locations/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/moto/locations/${id}`)
  },
}

// =============================================================================
// Moto Manufacturer API
// =============================================================================

export const motoManufacturerApi = {
  list: async (): Promise<ApiResponse<MotoManufacturer[]>> => {
    const response = await apiClient.get<ApiResponse<MotoManufacturer[]>>('/moto/manufacturers')
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<MotoManufacturer>> => {
    const response = await apiClient.get<ApiResponse<MotoManufacturer>>(`/moto/manufacturers/${id}`)
    return response.data
  },

  create: async (data: MotoManufacturerCreate): Promise<ApiResponse<MotoManufacturer>> => {
    const response = await apiClient.post<ApiResponse<MotoManufacturer>>('/moto/manufacturers', data)
    return response.data
  },

  update: async (id: number, data: MotoManufacturerUpdate): Promise<ApiResponse<MotoManufacturer>> => {
    const response = await apiClient.put<ApiResponse<MotoManufacturer>>(`/moto/manufacturers/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/moto/manufacturers/${id}`)
  },
}

// =============================================================================
// Moto Vendor API
// =============================================================================

export const vendorApi = {
  list: async (): Promise<ApiResponse<Vendor[]>> => {
    const response = await apiClient.get<ApiResponse<Vendor[]>>('/moto/vendors')
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<Vendor>> => {
    const response = await apiClient.get<ApiResponse<Vendor>>(`/moto/vendors/${id}`)
    return response.data
  },

  create: async (data: VendorCreate): Promise<ApiResponse<Vendor>> => {
    const response = await apiClient.post<ApiResponse<Vendor>>('/moto/vendors', data)
    return response.data
  },

  update: async (id: number, data: VendorUpdate): Promise<ApiResponse<Vendor>> => {
    const response = await apiClient.put<ApiResponse<Vendor>>(`/moto/vendors/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/moto/vendors/${id}`)
  },
}

// =============================================================================
// Motorcycle API
// =============================================================================

export const motorcycleApi = {
  /**
   * List all motorcycles (for sidebar)
   */
  list: async (): Promise<ApiResponse<MotorcycleSummary[]>> => {
    const response = await apiClient.get<ApiResponse<MotorcycleSummary[]>>('/motorcycles')
    return response.data
  },

  /**
   * Get a single motorcycle by ID
   */
  get: async (id: string): Promise<ApiResponse<Motorcycle>> => {
    const response = await apiClient.get<ApiResponse<Motorcycle>>(`/motorcycles/${id}`)
    return response.data
  },

  /**
   * Create a new motorcycle
   */
  create: async (data: MotorcycleCreate): Promise<ApiResponse<Motorcycle>> => {
    const response = await apiClient.post<ApiResponse<Motorcycle>>('/motorcycles', data)
    return response.data
  },

  /**
   * Update an existing motorcycle
   */
  update: async (id: string, data: MotorcycleUpdate): Promise<ApiResponse<Motorcycle>> => {
    const response = await apiClient.put<ApiResponse<Motorcycle>>(`/motorcycles/${id}`, data)
    return response.data
  },

  /**
   * Delete a motorcycle
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/motorcycles/${id}`)
  },

  // Service Records
  listServiceRecords: async (bikeId: string): Promise<ApiResponse<ServiceRecord[]>> => {
    const response = await apiClient.get<ApiResponse<ServiceRecord[]>>(`/motorcycles/${bikeId}/service`)
    return response.data
  },

  createServiceRecord: async (bikeId: string, data: ServiceRecordCreate): Promise<ApiResponse<ServiceRecord>> => {
    const response = await apiClient.post<ApiResponse<ServiceRecord>>(`/motorcycles/${bikeId}/service`, data)
    return response.data
  },

  updateServiceRecord: async (bikeId: string, recordId: number, data: ServiceRecordUpdate): Promise<ApiResponse<ServiceRecord>> => {
    const response = await apiClient.put<ApiResponse<ServiceRecord>>(`/motorcycles/${bikeId}/service/${recordId}`, data)
    return response.data
  },

  deleteServiceRecord: async (bikeId: string, recordId: number): Promise<void> => {
    await apiClient.delete(`/motorcycles/${bikeId}/service/${recordId}`)
  },

  // History Entries
  listHistoryEntries: async (bikeId: string): Promise<ApiResponse<HistoryEntry[]>> => {
    const response = await apiClient.get<ApiResponse<HistoryEntry[]>>(`/motorcycles/${bikeId}/history`)
    return response.data
  },

  createHistoryEntry: async (bikeId: string, data: HistoryEntryCreate): Promise<ApiResponse<HistoryEntry>> => {
    const response = await apiClient.post<ApiResponse<HistoryEntry>>(`/motorcycles/${bikeId}/history`, data)
    return response.data
  },

  updateHistoryEntry: async (bikeId: string, entryId: number, data: HistoryEntryUpdate): Promise<ApiResponse<HistoryEntry>> => {
    const response = await apiClient.put<ApiResponse<HistoryEntry>>(`/motorcycles/${bikeId}/history/${entryId}`, data)
    return response.data
  },

  deleteHistoryEntry: async (bikeId: string, entryId: number): Promise<void> => {
    await apiClient.delete(`/motorcycles/${bikeId}/history/${entryId}`)
  },

  // Parts
  listParts: async (bikeId: string): Promise<ApiResponse<Product[]>> => {
    const response = await apiClient.get<ApiResponse<Product[]>>(`/motorcycles/${bikeId}/parts`)
    return response.data
  },

  associatePart: async (bikeId: string, productId: number): Promise<void> => {
    await apiClient.post(`/motorcycles/${bikeId}/parts/${productId}`)
  },

  disassociatePart: async (bikeId: string, productId: number): Promise<void> => {
    await apiClient.delete(`/motorcycles/${bikeId}/parts/${productId}`)
  },

  // Build/Restoration Photos
  getBuildPhotos: async (bikeId: string): Promise<ApiResponse<{
    before: MotorcycleImage[]
    during: MotorcycleImage[]
    after: MotorcycleImage[]
    general: MotorcycleImage[]
  }>> => {
    const response = await apiClient.get(`/motorcycles/${bikeId}/build-photos`)
    return response.data
  },
}

// =============================================================================
// Asset Location API
// =============================================================================

export const assetLocationApi = {
  list: async (): Promise<ApiResponse<AssetLocationWithCount[]>> => {
    const response = await apiClient.get<ApiResponse<AssetLocationWithCount[]>>('/asset-locations')
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<AssetLocation>> => {
    const response = await apiClient.get<ApiResponse<AssetLocation>>(`/asset-locations/${id}`)
    return response.data
  },

  create: async (data: AssetLocationCreate): Promise<ApiResponse<AssetLocation>> => {
    const response = await apiClient.post<ApiResponse<AssetLocation>>('/asset-locations', data)
    return response.data
  },

  update: async (id: number, data: AssetLocationUpdate): Promise<ApiResponse<AssetLocation>> => {
    const response = await apiClient.put<ApiResponse<AssetLocation>>(`/asset-locations/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/asset-locations/${id}`)
  },
}

// =============================================================================
// Asset Manufacturer API
// =============================================================================

export const assetManufacturerApi = {
  list: async (): Promise<ApiResponse<AssetManufacturerWithCount[]>> => {
    const response = await apiClient.get<ApiResponse<AssetManufacturerWithCount[]>>('/asset-manufacturers')
    return response.data
  },

  get: async (id: number): Promise<ApiResponse<AssetManufacturer>> => {
    const response = await apiClient.get<ApiResponse<AssetManufacturer>>(`/asset-manufacturers/${id}`)
    return response.data
  },

  create: async (data: AssetManufacturerCreate): Promise<ApiResponse<AssetManufacturer>> => {
    const response = await apiClient.post<ApiResponse<AssetManufacturer>>('/asset-manufacturers', data)
    return response.data
  },

  update: async (id: number, data: AssetManufacturerUpdate): Promise<ApiResponse<AssetManufacturer>> => {
    const response = await apiClient.put<ApiResponse<AssetManufacturer>>(`/asset-manufacturers/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/asset-manufacturers/${id}`)
  },
}

// =============================================================================
// Asset API
// =============================================================================

export const assetApi = {
  /**
   * List assets with filtering and pagination
   */
  list: async (params?: AssetSearchParams): Promise<PaginatedResponse<Asset>> => {
    const response = await apiClient.get<PaginatedResponse<Asset>>('/assets', { params })
    return response.data
  },

  /**
   * Get a single asset by ID
   */
  get: async (id: string | number): Promise<ApiResponse<Asset>> => {
    const response = await apiClient.get<ApiResponse<Asset>>(`/assets/${id}`)
    return response.data
  },

  /**
   * Create a new asset
   */
  create: async (data: AssetCreate): Promise<ApiResponse<Asset>> => {
    const response = await apiClient.post<ApiResponse<Asset>>('/assets', data)
    return response.data
  },

  /**
   * Update an existing asset
   */
  update: async (id: string | number, data: AssetUpdate): Promise<ApiResponse<Asset>> => {
    const response = await apiClient.put<ApiResponse<Asset>>(`/assets/${id}`, data)
    return response.data
  },

  /**
   * Delete an asset
   */
  delete: async (id: string | number): Promise<void> => {
    await apiClient.delete(`/assets/${id}`)
  },

  /**
   * Get asset statistics for dashboard
   */
  getStats: async (): Promise<ApiResponse<AssetStats>> => {
    const response = await apiClient.get<ApiResponse<AssetStats>>('/assets/stats')
    return response.data
  },

  /**
   * Refresh status for a single asset via MCP integration
   */
  refreshStatus: async (id: string | number): Promise<ApiResponse<Asset>> => {
    const response = await apiClient.post<ApiResponse<Asset>>(`/assets/${id}/refresh-status`)
    return response.data
  },

  /**
   * Update asset status directly (for MCP webhook updates)
   */
  updateStatus: async (id: string | number, data: AssetStatusUpdate): Promise<ApiResponse<Asset>> => {
    const response = await apiClient.put<ApiResponse<Asset>>(`/assets/${id}/status`, data)
    return response.data
  },

  /**
   * Lookup device info by IP address using MCP tools
   * @param ipAddress - The IP address to lookup
   * @param source - Optional: restrict lookup to specific infrastructure ('proxmox', 'unifi', 'synology')
   */
  lookupByIP: async (ipAddress: string, source?: InfrastructureSource): Promise<ApiResponse<IPLookupResponse>> => {
    const response = await apiClient.post<ApiResponse<IPLookupResponse>>('/assets/lookup-by-ip', {
      ip_address: ipAddress,
      source: source || null,
    })
    return response.data
  },

  /**
   * Query UniFi gateway to discover all managed devices.
   * Returns devices with import status (new vs already imported).
   */
  queryUniFiGateway: async (): Promise<ApiResponse<QueryGatewayResponse>> => {
    const response = await apiClient.post<ApiResponse<QueryGatewayResponse>>('/assets/unifi/query-gateway')
    return response.data
  },

  /**
   * Get flat list of all storage drives across all assets (Disk Health page).
   */
  getDiskHealth: async (params?: DiskHealthSearchParams): Promise<PaginatedResponse<DiskHealthDrive>> => {
    const response = await apiClient.get<PaginatedResponse<DiskHealthDrive>>('/assets/disk-health', { params })
    return response.data
  },

  /**
   * Get summary statistics for disk health.
   */
  getDiskHealthStats: async (): Promise<ApiResponse<DiskHealthStats>> => {
    const response = await apiClient.get<ApiResponse<DiskHealthStats>>('/assets/disk-health/stats')
    return response.data
  },
}

// =============================================================================
// Credentials API
// =============================================================================

export const credentialsApi = {
  /**
   * Add Proxmox/PBS credentials by authenticating and creating an API token.
   * The token is stored in .env for future use.
   */
  addProxmoxCredentials: async (data: AddCredentialRequest): Promise<ApiResponse<CredentialResponse>> => {
    const response = await apiClient.post<ApiResponse<CredentialResponse>>('/credentials/proxmox', data)
    return response.data
  },

  /**
   * Test connection to a Proxmox/PBS host
   */
  testConnection: async (host: string, port: number = 8006): Promise<ApiResponse<{ host: string; port: number; reachable: boolean }>> => {
    const response = await apiClient.post<ApiResponse<{ host: string; port: number; reachable: boolean }>>('/credentials/test-connection', null, {
      params: { host, port },
    })
    return response.data
  },
}

// =============================================================================
// Tickets API
// =============================================================================

export const ticketApi = {
  list: async (params?: TicketSearchParams): Promise<PaginatedResponse<TicketSummary>> => {
    const response = await apiClient.get<PaginatedResponse<TicketSummary>>('/tickets', { params })
    return response.data
  },

  get: async (id: string | number): Promise<ApiResponse<Ticket>> => {
    const response = await apiClient.get<ApiResponse<Ticket>>(`/tickets/${id}`)
    return response.data
  },

  create: async (data: TicketCreate): Promise<ApiResponse<Ticket>> => {
    const response = await apiClient.post<ApiResponse<Ticket>>('/tickets', data)
    return response.data
  },

  update: async (id: string | number, data: TicketUpdate): Promise<ApiResponse<Ticket>> => {
    const response = await apiClient.put<ApiResponse<Ticket>>(`/tickets/${id}`, data)
    return response.data
  },

  delete: async (id: string | number): Promise<void> => {
    await apiClient.delete(`/tickets/${id}`)
  },

  getStats: async (): Promise<ApiResponse<TicketStats>> => {
    const response = await apiClient.get<ApiResponse<TicketStats>>('/tickets/stats')
    return response.data
  },

  getComments: async (ticketId: string | number): Promise<ApiResponse<TicketComment[]>> => {
    const response = await apiClient.get<ApiResponse<TicketComment[]>>(`/tickets/${ticketId}/comments`)
    return response.data
  },

  addComment: async (ticketId: string | number, data: TicketCommentCreate): Promise<ApiResponse<TicketComment>> => {
    const response = await apiClient.post<ApiResponse<TicketComment>>(`/tickets/${ticketId}/comments`, data)
    return response.data
  },

  // Attachment methods
  uploadAttachment: async (ticketId: string | number, file: File): Promise<ApiResponse<TicketAttachment>> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<ApiResponse<TicketAttachment>>(
      `/tickets/${ticketId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  deleteAttachment: async (ticketId: string | number, attachmentId: number): Promise<void> => {
    await apiClient.delete(`/tickets/${ticketId}/attachments/${attachmentId}`)
  },

  getAttachmentDownloadUrl: (ticketId: string | number, attachmentId: number): string => {
    return `${API_BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/download`
  },
}


// =============================================================================
// Timelog API
// =============================================================================

export const timelogApi = {
  // Customers
  listCustomers: async (): Promise<ApiResponse<TimelogCustomer[]>> => {
    const response = await apiClient.get<ApiResponse<TimelogCustomer[]>>('/timelog/customers')
    return response.data
  },

  createCustomer: async (data: TimelogCustomerCreate): Promise<ApiResponse<TimelogCustomer>> => {
    const response = await apiClient.post<ApiResponse<TimelogCustomer>>('/timelog/customers', data)
    return response.data
  },

  updateCustomer: async (id: number, data: TimelogCustomerUpdate): Promise<ApiResponse<TimelogCustomer>> => {
    const response = await apiClient.put<ApiResponse<TimelogCustomer>>(`/timelog/customers/${id}`, data)
    return response.data
  },

  deleteCustomer: async (id: number): Promise<void> => {
    await apiClient.delete(`/timelog/customers/${id}`)
  },

  // Entries
  listEntries: async (params?: TimelogSearchParams): Promise<PaginatedResponse<TimelogEntry>> => {
    const response = await apiClient.get<PaginatedResponse<TimelogEntry>>('/timelog/entries', { params })
    return response.data
  },

  createEntry: async (data: TimelogEntryCreate): Promise<ApiResponse<TimelogEntry>> => {
    const response = await apiClient.post<ApiResponse<TimelogEntry>>('/timelog/entries', data)
    return response.data
  },

  updateEntry: async (id: number, data: TimelogEntryUpdate): Promise<ApiResponse<TimelogEntry>> => {
    const response = await apiClient.put<ApiResponse<TimelogEntry>>(`/timelog/entries/${id}`, data)
    return response.data
  },

  deleteEntry: async (id: number): Promise<void> => {
    await apiClient.delete(`/timelog/entries/${id}`)
  },

  // Report PDF URL (opens in new tab)
  getReportUrl: (year: number, month: number, customerId?: number): string => {
    let url = `${API_BASE_URL}/timelog/report?year=${year}&month=${month}`
    if (customerId) url += `&customer_id=${customerId}`
    return url
  },
}

export default apiClient
